import * as React from "react"
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import type { ReactNode } from 'react'
import * as authApi from './api'
import type { AuthUser, Role } from './types'
import { ApiError } from '@/lib/api'

interface AuthState {
  user: AuthUser | null
  loading: boolean
  error: string | null
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<AuthUser>
  logout: () => Promise<void>
  hasRole: (...allowed: Role[]) => boolean
  refresh: () => Promise<void>
}

const Ctx = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  })

  const refresh = useCallback(async () => {
    try {
      const user = await authApi.getCurrentUser()
      setState({ user, loading: false, error: null })
    } catch (err) {
      // 401 is the normal "logged out" state — not an error to surface.
      if (err instanceof ApiError && err.isAuthError) {
        setState({ user: null, loading: false, error: null })
      } else {
        setState({
          user: null,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to load session',
        })
      }
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const login = useCallback(
    async (email: string, password: string) => {
      setState((s) => ({ ...s, loading: true, error: null }))
      const user = await authApi.login({ email, password })
      setState({ user, loading: false, error: null })
      return user
    },
    [],
  )

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } finally {
      setState({ user: null, loading: false, error: null })
    }
  }, [])

  const hasRole = useCallback(
    (...allowed: Role[]) => {
      if (!state.user) return false
      return allowed.includes(state.user.role)
    },
    [state.user],
  )

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, hasRole, refresh }),
    [state, login, logout, hasRole, refresh],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
